import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useState, useEffect } from 'react'
import { Link, useLocation } from 'wouter'
import { Mail, Lock, Eye, EyeOff, User, Building, Briefcase, CheckSquare, Square } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuth } from '@/contexts/auth-context'
import { buildApiUrl } from '@/config/api'
import { getGclidForSubmission } from '@/lib/gclid'

export function Register() {
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isLinkedInLoading, setIsLinkedInLoading] = useState(false)
  const [agreeToTerms, setAgreeToTerms] = useState(false)
  const [, setLocation] = useLocation()
  const { register } = useAuth()
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    company: '',
    role: '',
    gclid: '', // Google Click ID for attribution tracking
  })

  // Capture GCLID from URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const gclid = urlParams.get('gclid')
    
    if (gclid) {
      setFormData(prev => ({ ...prev, gclid }))
      console.log('📊 GCLID captured:', gclid)
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!agreeToTerms) {
      toast.error('You must agree to the Terms of Service to continue')
      return
    }
    
    setIsLoading(true)

    // Get GCLID for submission (captures from URL or uses stored value)
    const gclid = getGclidForSubmission()
    const registrationData = { ...formData, gclid }

    const result = await register(registrationData)

    if (result.success) {
      toast.success(result.message)
      setLocation('/')
    } else {
      toast.error(result.message)
    }

    setIsLoading(false)
  }

  const handleLinkedInSignup = async () => {
    setIsLinkedInLoading(true)
    try {
      const response = await fetch(buildApiUrl('/api/auth/linkedin/url'))
      const data = await response.json()
      
      if (data.success) {
        window.location.href = data.data.authUrl
      } else {
        toast.error('Failed to initiate LinkedIn signup')
      }
    } catch (error) {
      console.error('LinkedIn signup error:', error)
      toast.error('Failed to connect to LinkedIn')
    } finally {
      setIsLinkedInLoading(false)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-primary/10 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-lg gradient-bg flex items-center justify-center">
            <span className="text-white font-bold text-lg">SI</span>
          </div>
          <CardTitle className="text-2xl">Create Account</CardTitle>
          <CardDescription>
            Sign up for SourcedIn to start managing your RFPs
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Full Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter your full name"
                  className="w-full pl-10 pr-4 py-2 border border-input rounded-md bg-background"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Enter your email"
                  className="w-full pl-10 pr-4 py-2 border border-input rounded-md bg-background"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Create a password"
                  className="w-full pl-10 pr-10 py-2 border border-input rounded-md bg-background"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Company</label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => handleInputChange('company', e.target.value)}
                  placeholder="Enter your company name"
                  className="w-full pl-10 pr-4 py-2 border border-input rounded-md bg-background"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Role</label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <select
                  value={formData.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-input rounded-md bg-background"
                  required
                >
                  <option value="">Select your role</option>
                  <option value="Purchase Manager">Purchase Manager</option>
                  <option value="Procurement Specialist">Procurement Specialist</option>
                  <option value="Business Analyst">Business Analyst</option>
                  <option value="Project Manager">Project Manager</option>
                  <option value="Director">Director</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            {/* Terms of Service Checkbox */}
            <div className="flex items-start space-x-3">
              <button
                type="button"
                onClick={() => setAgreeToTerms(!agreeToTerms)}
                className="flex-shrink-0 mt-1"
              >
                {agreeToTerms ? (
                  <CheckSquare className="h-5 w-5 text-primary" />
                ) : (
                  <Square className="h-5 w-5 text-muted-foreground border-2 border-input rounded" />
                )}
              </button>
              <div className="text-sm text-muted-foreground">
                <span>I agree to the </span>
                <Link href="/terms" className="text-primary hover:underline font-medium">
                  Terms of Service
                </Link>
                <span> and </span>
                <Link href="/privacy" className="text-primary hover:underline font-medium">
                  Privacy Policy
                </Link>
                <span>.</span>
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={isLoading || !agreeToTerms}>
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
            </div>
          </div>

          {/* LinkedIn OAuth Button */}
          <Button
            type="button"
            variant="outline"
            className="w-full bg-[#0077B5] text-white border-[#0077B5] hover:bg-[#006097] hover:border-[#006097]"
            onClick={handleLinkedInSignup}
            disabled={isLinkedInLoading}
          >
            {isLinkedInLoading ? (
              'Connecting to LinkedIn...'
            ) : (
              <>
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                Continue with LinkedIn
              </>
            )}
          </Button>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link href="/login" className="text-primary hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
} 